// @greenlight-spec: 19-design-system.md
import * as React from 'react';

import { cn } from '@/lib/utils';

export type SegTone = 'green' | 'amber' | 'red' | 'neutral';

export interface HealthSegment {
  key: string;
  label: string;
  count: number;
  tone: SegTone;
  hint?: string;
}

export interface SegmentedHealthProps {
  title: string;
  total: number;
  unitLabel?: string;
  segments: HealthSegment[];
  className?: string;
  'data-testid'?: string;
}

const DOT_CLASS: Record<SegTone, string> = {
  green: 'bg-gl-green',
  amber: 'bg-gl-amber',
  red: 'bg-gl-red',
  neutral: 'bg-slate',
};

const VALUE_CLASS: Record<SegTone, string> = {
  green: 'text-gl-green-text',
  amber: 'text-gl-amber-text',
  red: 'text-gl-red-text',
  neutral: 'text-muted-foreground',
};

const BAR_CLASS: Record<SegTone, string> = {
  green: 'bg-gl-green',
  amber: 'bg-gl-amber',
  red: 'bg-gl-red',
  // Match the neutral dot (--text-tertiary), not the bar's own bg-muted track —
  // otherwise the paused/inactive segment is invisible against the track.
  neutral: 'bg-slate',
};

export function SegmentedHealth(props: SegmentedHealthProps): React.JSX.Element {
  const { title, total, unitLabel, segments, className, 'data-testid': testId } = props;

  return (
    <div
      className={cn('rounded-gl-lg border border-border bg-card p-4', className)}
      data-testid={testId}
    >
      {/* Header row */}
      <div className="mb-3 flex items-center justify-between">
        <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
          {title}
        </span>
        <span className="text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">{total}</span>
          {unitLabel ? ` ${unitLabel}` : ''}
        </span>
      </div>

      {/* Proportion bar */}
      <div className="mb-4 flex h-2 w-full overflow-hidden rounded-full bg-muted">
        {total > 0 &&
          segments.map((seg) => {
            if (seg.count === 0) return null;
            return (
              <div
                key={seg.key}
                className={cn('h-full', BAR_CLASS[seg.tone])}
                style={{ width: `${(seg.count / total) * 100}%` }}
                aria-hidden="true"
              />
            );
          })}
      </div>

      {/* Tiles grid — hairline seams via bg-border + gap-px */}
      <div className="overflow-hidden rounded-[8px] border border-border bg-border">
        <div className="grid grid-cols-2 gap-px md:grid-cols-4">
          {segments.map((seg) => (
            <div key={seg.key} className="bg-card px-4 py-3">
              <div className="mb-1.5 flex items-center gap-1.5">
                <span
                  className={cn('size-1.5 shrink-0 rounded-full', DOT_CLASS[seg.tone])}
                  aria-hidden="true"
                />
                <span className="font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
                  {seg.label}
                </span>
              </div>
              <div className={cn('text-2xl font-semibold', VALUE_CLASS[seg.tone])}>{seg.count}</div>
              {seg.hint && <div className="mt-1 text-[11px] text-muted-foreground">{seg.hint}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
